
import React from 'react';
import { createRoot } from 'react-dom/client';
import ReactGA from 'react-ga4';
import App from './App.tsx';
import './index.css';

// Run client env validation as early as possible
import { initializeClientEnvironment } from './lib/client-env-validator';
// Initialize Sentry as early as possible (gated on consent in production)
import { initializeSentry, captureMessage } from './lib/sentry-config';
import { setupEarlyErrorBuffer, getAndClearEarlyErrors } from './lib/sentry-early';
import { hasAnalyticsConsent } from './lib/consent';

// Build marker for deployment verification
 
(window as any).__BUILD_INFO = {
  commit: import.meta.env.VITE_GIT_COMMIT || 'unknown',
  builtAt: import.meta.env.VITE_BUILD_TIME || 'unknown',
  featureFlags: {
    AUTH_READINESS: true,
  },
};

// Validate client environment, attach early error listeners, then initialize Sentry lazily in prod
initializeClientEnvironment();
if (import.meta.env.PROD) {
  setupEarlyErrorBuffer();
  // Only initialize Sentry + GA4 if user has already granted analytics consent.
  // If no consent yet, ConsentContext will call initializeSentry() / ReactGA.initialize()
  // when consent is later granted.
  if (hasAnalyticsConsent()) {
    setTimeout(() => {
      try {
        initializeSentry();
        // Flush early buffered errors through Sentry if available
        const queued = getAndClearEarlyErrors();
        if (queued.length > 0) {
          queued.forEach((rec) => {
            captureMessage(`Early ${rec.type}: ${rec.message}`, 'error');
          });
        }
      } catch {
        // ignore
      }
      // GA4: update Consent Mode and initialise
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('consent', 'update', { 'analytics_storage': 'granted' });
        }
        const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
        if (measurementId) {
          ReactGA.initialize(measurementId);
        }
      } catch {
        // ignore — GA4 failure should not block app startup
      }
    }, 0);
  }
} else {
  // In dev, initialize immediately for DX (PECR only applies to production)
  initializeSentry();
}

// Mount the app to the DOM
const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container not found");
}

const root = createRoot(container);
root.render(<App />);
